import { StandardEvents } from '@shopify/events';

const ENTER_CLASS = 'cgau-filter-result-enter';
const CONFIRM_CLASS = 'cgau-compare-selection-confirmed';
const THUMB_CLASS = 'cgau-compare-tray__thumbs';
const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
const shortlistMedia = new Map();
const timers = new Set();
let filterVersion = 0;

const motionObserver = typeof IntersectionObserver === 'function'
  ? new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (entry.isIntersecting) return;
    entry.target.classList.remove(ENTER_CLASS, CONFIRM_CLASS, 'is-arriving');
    motionObserver.unobserve(entry.target);
  }))
  : null;

const motionAllowed = (element) => {
  if (document.hidden || reduceMotion?.matches || !(element instanceof Element)) return false;
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < globalThis.innerHeight && rect.right > 0 && rect.left < globalThis.innerWidth;
};

const clearMotionClass = (element, className, delay = 600) => {
  const timer = globalThis.setTimeout(() => {
    timers.delete(timer);
    element?.classList.remove(className);
    if (element && !element.matches(`.${ENTER_CLASS}, .${CONFIRM_CLASS}, .is-arriving`)) motionObserver?.unobserve(element);
  }, delay);
  timers.add(timer);
};

const observeMotion = (element) => motionObserver?.observe(element);

const selectionFromButton = (button) => {
  const id = String(button?.dataset.id || '');
  const image = String(button?.dataset.image || '');
  if (!/^\d+$/.test(id) || !image) return null;
  return { id, image, title: String(button.dataset.title || 'Selected product').slice(0, 220) };
};

const renderTrayThumbnails = () => {
  const comparison = document.querySelector('cgau-product-comparison');
  const tray = comparison?.querySelector('[data-compare-tray]');
  if (!tray) return;
  let strip = tray.querySelector(`.${THUMB_CLASS}`);
  if (!strip) {
    strip = document.createElement('span');
    strip.className = THUMB_CLASS;
    strip.setAttribute('aria-hidden', 'true');
    tray.insertBefore(strip, tray.firstChild);
  }

  const selectedOrder = comparison.selected instanceof Map ? [...comparison.selected.keys()].map(String) : [...shortlistMedia.keys()];
  const media = selectedOrder.map((id) => shortlistMedia.get(id)).filter(Boolean).slice(0, 3);
  const fragment = document.createDocumentFragment();
  media.forEach((item) => {
    const frame = document.createElement('span');
    frame.className = 'cgau-compare-tray__thumb';
    frame.dataset.cgauCompareThumb = item.id;
    const image = document.createElement('img');
    image.src = item.image;
    image.alt = '';
    image.width = 42;
    image.height = 42;
    image.loading = 'eager';
    frame.append(image);
    fragment.append(frame);
  });
  strip.replaceChildren(fragment);
  strip.hidden = media.length === 0;
};

const syncVisibleSelections = () => {
  const comparison = document.querySelector('cgau-product-comparison');
  const selectedIds = comparison?.selected instanceof Map
    ? new Set([...comparison.selected.keys()].map(String))
    : new Set([...document.querySelectorAll('[data-cgau-compare][aria-pressed="true"]')].map((button) => String(button.dataset.id || '')));
  [...shortlistMedia.keys()].forEach((id) => { if (!selectedIds.has(id)) shortlistMedia.delete(id); });
  document.querySelectorAll('[data-cgau-compare][aria-pressed="true"]').forEach((button) => {
    const item = selectionFromButton(button);
    if (item) shortlistMedia.set(item.id, item);
  });
  renderTrayThumbnails();
};

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const compareButton = target?.closest('[data-cgau-compare]');
  const clearButton = target?.closest('[data-compare-clear]');
  const removeButton = target?.closest('[data-compare-remove]');

  queueMicrotask(() => globalThis.requestAnimationFrame(() => {
    if (clearButton) shortlistMedia.clear();
    if (removeButton) shortlistMedia.delete(String(removeButton.dataset.compareRemove || ''));
    if (compareButton) {
      const item = selectionFromButton(compareButton);
      const selected = compareButton.getAttribute('aria-pressed') === 'true';
      if (item && selected) shortlistMedia.set(item.id, item);
      if (item && !selected) shortlistMedia.delete(item.id);
      const card = compareButton.closest('.product-grid__item');
      if (selected && motionAllowed(card)) {
        card.classList.remove(CONFIRM_CLASS);
        void card.offsetWidth;
        card.classList.add(CONFIRM_CLASS);
        observeMotion(card);
        clearMotionClass(card, CONFIRM_CLASS);
      }
    }
    syncVisibleSelections();
    const newest = compareButton && shortlistMedia.get(String(compareButton.dataset.id || ''));
    const thumb = newest && document.querySelector(`[data-cgau-compare-thumb="${CSS.escape(newest.id)}"]`);
    if (thumb && motionAllowed(thumb)) {
      thumb.classList.add('is-arriving');
      observeMotion(thumb);
      clearMotionClass(thumb, 'is-arriving', 520);
    }
  }));
});

document.addEventListener(StandardEvents.collectionUpdate, (event) => {
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  const sectionId = section?.id;
  const oldGrid = section?.querySelector('[data-testid="product-grid"]');
  if (!sectionId || !oldGrid) return;
  const oldOrder = new Map(
    [...oldGrid.querySelectorAll(':scope > .product-grid__item[data-product-id]')]
      .map((card, index) => [String(card.dataset.productId), index])
  );
  const version = ++filterVersion;

  event.promise?.then((result) => {
    if (result?.detail?.didError === true || result?.didError === true) return;
    if (version !== filterVersion) return;
    globalThis.requestAnimationFrame(() => {
      const newSection = document.getElementById(sectionId);
      const newGrid = newSection?.querySelector('[data-testid="product-grid"]');
      if (!newGrid) return;
      const cards = [...newGrid.querySelectorAll(':scope > .product-grid__item[data-product-id]')];
      cards.forEach((card, index) => {
        const changed = oldOrder.get(String(card.dataset.productId)) !== index;
        if (!changed || !motionAllowed(card)) return;
        card.classList.add(ENTER_CLASS);
        observeMotion(card);
        clearMotionClass(card, ENTER_CLASS, 520);
      });
      syncVisibleSelections();
    });
  }).catch(() => {});
});

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) return;
  timers.forEach((timer) => globalThis.clearTimeout(timer));
  timers.clear();
  document.querySelectorAll(`.${ENTER_CLASS}, .${CONFIRM_CLASS}, .cgau-compare-tray__thumb.is-arriving`).forEach((element) => {
    element.classList.remove(ENTER_CLASS, CONFIRM_CLASS, 'is-arriving');
    motionObserver?.unobserve(element);
  });
});

reduceMotion?.addEventListener?.('change', () => {
  if (!reduceMotion.matches) return;
  timers.forEach((timer) => globalThis.clearTimeout(timer));
  timers.clear();
  document.querySelectorAll(`.${ENTER_CLASS}, .${CONFIRM_CLASS}, .cgau-compare-tray__thumb.is-arriving`).forEach((element) => {
    element.classList.remove(ENTER_CLASS, CONFIRM_CLASS, 'is-arriving');
    motionObserver?.unobserve(element);
  });
});

const init = () => customElements.whenDefined('cgau-product-comparison').then(() => {
  globalThis.requestAnimationFrame(syncVisibleSelections);
  globalThis.addEventListener('pageshow', () => globalThis.requestAnimationFrame(syncVisibleSelections));
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
